// Default-filter enforcement for the TANC grid.
//
// A TANC filter (TANC Manager -> Filters) can pin a column to fixed values
// ("Values" in the UI, `field_tokens` in the saved config). TANCSession turns
// those into a default AG Grid filter model and puts it back whenever the user
// removes or changes it. These helpers decide WHAT to put back, comparing the
// models by meaning rather than by serialised shape: AG Grid hands the model
// back in its own shape (it adds `null` slots to multi-filter `filterModels`,
// and may drop a set filter that selects every value), and a shape-only
// comparison made the enforcement re-apply itself forever. Every re-apply
// re-filtered the grid and closed any open cell editor, which is the "bounced
// out of the dropdown" prod bug (Asana 1219752595266627).

const isSetModel = (m) => m != null && m.filterType === 'set';

// The sub-models of a column filter model, with the null slots AG Grid adds
// removed. A bare (non-multi) model is treated as a single sub-model.
const subModelsOf = (colModel) => {
    if (colModel == null) return [];
    if (Array.isArray(colModel.filterModels)) {
        return colModel.filterModels.filter((m) => m != null);
    }
    return [colModel];
};

// The set-filter values of a column filter model, as a sorted array of
// strings, or null when the column has no set filter (or its values are null,
// which AG Grid uses for "nothing restricted").
export const getSetFilterValues = (colModel) => {
    const setModel = subModelsOf(colModel).find(isSetModel);
    if (!setModel || !Array.isArray(setModel.values)) return null;
    return setModel.values.map((v) => (v == null ? '' : String(v))).sort();
};

const sameValues = (a, b) => {
    if (a == null || b == null) return a == null && b == null;
    if (a.length !== b.length) return false;
    return a.every((v, i) => v === b[i]);
};

// True when `colModel` already carries the default set-filter values that
// `defaultColModel` enforces. Extra non-set sub-filters the user added on top
// (text, number, date) are allowed and do not count as a change.
export const isDefaultSatisfied = (colModel, defaultColModel) =>
    sameValues(getSetFilterValues(colModel), getSetFilterValues(defaultColModel));

// Work out the model to apply so every default-filtered column carries its
// default set values again.
//
//   currentModel  - api.getFilterModel()
//   defaultModel  - the TANC default filter model (tancFilterModel)
//   changedField  - the column the triggering filterChanged event was about
//
// Returns { model, needsApply, defaultValuesAltered }:
//   model                - a fresh object safe to pass to setFilterModel
//   needsApply           - false when nothing has to change (do not re-apply)
//   defaultValuesAltered - the user edited the default values of changedField
//                          (the caller warns that this is not allowed)
export const computeEnforcedFilterModel = (currentModel, defaultModel, changedField) => {
    const model = JSON.parse(JSON.stringify(currentModel || {}));
    const defaults = defaultModel || {};
    let needsApply = false;
    let defaultValuesAltered = false;

    for (const colName of Object.keys(defaults)) {
        const defaultColModel = defaults[colName];
        const defaultSet = subModelsOf(defaultColModel).find(isSetModel);
        if (!defaultSet) continue;

        if (!Object.prototype.hasOwnProperty.call(model, colName) || model[colName] == null) {
            // The default filter for this column was removed: put it back whole.
            model[colName] = JSON.parse(JSON.stringify(defaultColModel));
            needsApply = true;
            continue;
        }
        if (isDefaultSatisfied(model[colName], defaultColModel)) continue;

        // The set values were changed (or the set sub-filter was removed):
        // restore the default set filter and keep the user's other sub-filters.
        if (colName === changedField && getSetFilterValues(model[colName]) != null) {
            defaultValuesAltered = true;
        }
        const others = subModelsOf(model[colName]).filter((m) => !isSetModel(m));
        model[colName] = {
            filterType: 'multi',
            filterModels: [JSON.parse(JSON.stringify(defaultSet)), ...others],
        };
        needsApply = true;
    }

    return { model, needsApply, defaultValuesAltered };
};

// A stable string for a filter model, independent of key order and of the
// null slots AG Grid inserts, so two models that mean the same thing compare
// equal. Used to recognise "the grid came back in the same state we already
// tried to fix", which is where the old code looped.
export const filterModelSignature = (model) => {
    const normalise = (value) => {
        if (Array.isArray(value)) return value.filter((v) => v != null).map(normalise);
        if (value && typeof value === 'object') {
            return Object.keys(value).sort().reduce((acc, key) => {
                acc[key] = normalise(value[key]);
                return acc;
            }, {});
        }
        return value;
    };
    return JSON.stringify(normalise(model || {}));
};
